<template>
  <b-modal :visible="!!problemId" size="xl" scrollable hide-footer body-class="ppd-body"
           :title="title" @hidden="$emit('close')">
    <div v-if="loading" class="text-muted"><b-spinner small /> Loading every detail…</div>
    <div v-else-if="error" class="text-danger">{{ error }}</div>
    <div v-else-if="d">
      <!-- 1. The failure -->
      <div class="ppd-hero">
        <div>
          <div class="ppd-kicker">{{ kindLabel(f.kind) }} · {{ when(f.createdAt) }}</div>
          <h3 class="ppd-title">{{ f.label }}</h3>
          <div class="ppd-chips">
            <code class="ppd-code" v-if="f.code">{{ f.code }}</code>
            <span class="trk-pill" v-if="f.paymentFor">{{ f.paymentFor }}</span>
            <span class="trk-pill" v-if="f.source">{{ sourceLabel(f.source) }}</span>
            <span class="trk-pill" v-if="f.amountPence">{{ money(f.amountPence) }}</span>
            <span class="trk-pill" :class="f.alerted ? 'info' : ''">{{ f.alerted ? 'Alert email sent' : 'No alert email' }}</span>
          </div>
        </div>
        <button type="button" class="btn trk-btn-ghost btn-sm" @click="copyAll"><i class="fa fa-clone"></i> Copy all details</button>
      </div>

      <div class="ppd-advice" v-if="f.advice">
        <i class="fa fa-lightbulb-o"></i> <div><strong>What to do</strong><div>{{ f.advice }}</div></div>
      </div>

      <div class="ppd-grid">
        <!-- 2. Exact error -->
        <section class="ppd-card ppd-wide">
          <h4><i class="fa fa-bug"></i> Exact error</h4>
          <dl class="ppd-dl">
            <dt>Reason</dt><dd>{{ f.label }}</dd>
            <dt>Code</dt><dd><code>{{ f.code || '—' }}</code></dd>
            <dt>Message</dt><dd class="text-danger">{{ f.message || '—' }}</dd>
            <template v-for="row in stripeRows">
              <dt :key="'k' + row.key">{{ row.label }}</dt>
              <dd :key="'v' + row.key"><code v-if="row.code">{{ row.value }}</code><span v-else>{{ row.value }}</span></dd>
            </template>
            <template v-if="d.stripe">
              <dt>Page</dt><dd><code>{{ d.stripe.page || '—' }}</code></dd>
              <dt>Reported at</dt><dd>{{ when(d.stripe.occurredAt) }}</dd>
            </template>
          </dl>
          <p class="text-muted small mb-0" v-if="!d.stripe && f.kind === 'CHECKOUT'">
            No matching tracking event was found for this checkout.
          </p>
          <details class="ppd-raw" v-if="d.stripe && d.stripe.metadata">
            <summary>Raw data from the patient's browser</summary>
            <pre>{{ pretty(d.stripe.metadata) }}</pre>
          </details>
        </section>

        <!-- 3. Who -->
        <section class="ppd-card">
          <h4><i class="fa fa-user"></i> Patient</h4>
          <dl class="ppd-dl" v-if="d.patient">
            <dt>Name</dt><dd><router-link :to="'/patient/' + d.patient.id">{{ d.patient.name || ('Patient #' + d.patient.id) }}</router-link></dd>
            <dt>Email</dt><dd>{{ d.patient.email || '—' }}</dd>
            <dt>Phone</dt><dd>{{ d.patient.phone || '—' }}</dd>
            <dt>Date of birth</dt><dd>{{ d.patient.dateOfBirth || '—' }}</dd>
            <dt>Saved cards</dt><dd>{{ d.patient.savedCards }}</dd>
            <dt>Stripe customer</dt><dd><code>{{ d.patient.stripeCustomerId || '—' }}</code></dd>
          </dl>
          <template v-else-if="d.journey && (d.journey.leadEmail || d.journey.leadName)">
            <p class="text-muted small">Not signed in yet — details typed on the form:</p>
            <dl class="ppd-dl">
              <dt>Name</dt><dd>{{ d.journey.leadName || '—' }}</dd>
              <dt>Email</dt><dd>{{ d.journey.leadEmail || '—' }}</dd>
              <dt>Phone</dt><dd>{{ d.journey.leadPhone || '—' }}</dd>
            </dl>
          </template>
          <p v-else class="text-muted small mb-0">The patient hadn't signed in or typed their details yet.</p>
        </section>

        <!-- 4. Order -->
        <section class="ppd-card">
          <h4><i class="fa fa-file-text-o"></i> Order</h4>
          <dl class="ppd-dl" v-if="d.order">
            <dt>Order</dt><dd><router-link :to="orderLink">#{{ d.order.id }}</router-link></dd>
            <dt>Status</dt><dd><code>{{ d.order.status }}</code></dd>
            <dt>Type</dt><dd>{{ d.order.type }}<span v-if="d.order.mediaType"> · {{ d.order.mediaType }}</span></dd>
            <dt>Created</dt><dd>{{ when(d.order.createdAt) }}</dd>
            <dt>Card on order</dt><dd>{{ d.order.hasPaymentMethod ? 'Yes' : 'No' }}</dd>
          </dl>
          <p v-else class="text-muted small mb-0">No order was created — the payment failed before booking.</p>
        </section>

        <!-- 5. Device & journey -->
        <section class="ppd-card">
          <h4><i class="fa fa-mobile"></i> Device &amp; journey</h4>
          <dl class="ppd-dl" v-if="d.journey">
            <dt>Device</dt><dd>{{ d.journey.deviceType || '—' }}</dd>
            <dt>Browser</dt><dd :title="d.journey.userAgent">{{ browser(d.journey.userAgent) }}</dd>
            <dt>Platform</dt><dd>{{ d.journey.platform || '—' }}</dd>
            <dt>Patient type</dt><dd>{{ d.journey.userType || '—' }}</dd>
            <dt>Started</dt><dd>{{ when(d.journey.startedAt) }}</dd>
            <dt>Last active</dt><dd>{{ when(d.journey.lastActivityAt) }}</dd>
            <dt>Now at</dt><dd>{{ d.journey.currentStep || '—' }} <span class="text-muted">({{ d.journey.status }})</span></dd>
            <dt>Came from</dt><dd>{{ source(d.journey) }}</dd>
          </dl>
          <router-link v-if="d.journey" class="btn trk-btn-ghost btn-sm mt-2" :to="'/tracking/sessions/' + d.journey.sessionId">
            Open full journey
          </router-link>
          <p v-else class="text-muted small mb-0">Not linked to a tracked journey.</p>
        </section>

        <!-- 6. Tries in this checkout -->
        <section class="ppd-card" v-if="d.tries && d.tries.length">
          <h4><i class="fa fa-repeat"></i> Tries in this checkout ({{ d.tries.length }})</h4>
          <ol class="ppd-tries">
            <li v-for="t in d.tries" :key="t.id" :class="{ current: t.current }">
              <span class="text-muted">{{ when(t.createdAt) }}</span>
              {{ t.label }} <code v-if="t.code">{{ t.code }}</code>
              <span v-if="t.current" class="trk-pill info">this one</span>
            </li>
          </ol>
        </section>

        <!-- 7. Recovery -->
        <section class="ppd-card ppd-wide" v-if="d.recovery">
          <h4><i class="fa fa-medkit"></i> Recovery <span class="trk-pill">{{ d.recovery.state }}</span></h4>
          <p class="small mb-2">
            {{ d.recovery.autoAttempts }} automatic tries
            <span v-if="d.recovery.nextAttemptAt"> · next {{ when(d.recovery.nextAttemptAt) }}</span>
            <span v-if="d.recovery.linkSentAt"> · link sent {{ when(d.recovery.linkSentAt) }}</span>
            <span v-if="d.recovery.recoveredAt"> · paid {{ when(d.recovery.recoveredAt) }} by {{ d.recovery.recoveredBy }}</span>
          </p>
          <table class="table table-sm mb-0" v-if="d.recovery.attempts.length">
            <thead><tr><th>When</th><th>How</th><th>By</th><th>Result</th><th>Stripe</th></tr></thead>
            <tbody>
              <tr v-for="(a, i) in d.recovery.attempts" :key="i">
                <td class="text-nowrap">{{ when(a.createdAt) }}</td>
                <td>{{ a.method }}</td>
                <td>{{ a.attemptedBy || a.trigger }}</td>
                <td>
                  <span :class="a.succeeded ? 'text-success' : 'text-danger'">{{ a.succeeded ? 'Worked' : 'Failed' }}</span>
                  <div class="small" v-if="!a.succeeded"><code v-if="a.failureCode">{{ a.failureCode }}</code> {{ a.failureMessage }}</div>
                </td>
                <td><code class="small">{{ a.stripeId || '—' }}</code></td>
              </tr>
            </tbody>
          </table>
          <details class="ppd-raw" v-if="d.recovery.lastFailureDetail">
            <summary>Stripe's full breakdown of the last failure</summary>
            <pre>{{ pretty(d.recovery.lastFailureDetail) }}</pre>
          </details>
        </section>

        <!-- 8. Timeline -->
        <section class="ppd-card ppd-wide">
          <h4><i class="fa fa-history"></i> What happened before it failed</h4>
          <div v-if="!d.timeline || !d.timeline.length" class="text-muted small">No journey steps recorded around this time.</div>
          <div v-else class="trk-table-scroll">
            <table class="table table-sm mb-0 ppd-timeline">
              <thead><tr><th>Time</th><th>What</th><th>Step</th><th>API</th><th>Result</th></tr></thead>
              <tbody>
                <tr v-for="e in d.timeline" :key="e.id" :class="{ 'ppd-hit': e.isThisFailure, 'ppd-bad': e.outcome === 'FAILURE' && !e.isThisFailure }">
                  <td class="text-nowrap">{{ time(e.at) }}</td>
                  <td>
                    <strong>{{ humanise(e.event) }}</strong>
                    <div class="small text-danger" v-if="e.message">{{ e.message }}</div>
                  </td>
                  <td class="small">{{ humanise(e.step) }}</td>
                  <td class="small">
                    <code v-if="e.apiPath">{{ e.apiPath }}</code>
                    <span v-if="e.durationMs" class="text-muted"> · {{ e.durationMs }}ms</span>
                  </td>
                  <td class="small">
                    <span v-if="e.httpStatus" :class="e.httpStatus >= 400 ? 'text-danger' : ''">HTTP {{ e.httpStatus }}</span>
                    <code v-if="e.errorCode">{{ e.errorCode }}</code>
                    <span v-if="!e.httpStatus && !e.errorCode" class="text-muted">{{ (e.outcome || '').toLowerCase() }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </b-modal>
</template>

<script>
import moment from 'moment';

/** Stripe fields the patient's browser reports, in the order admins care about. */
const STRIPE_FIELDS = [
  ['declineCode', 'Bank decline code', true],
  ['stripeCode', 'Stripe error code', true],
  ['stripeErrorType', 'Stripe error type', true],
  ['paymentIntentStatus', 'Payment status', true],
  ['methodType', 'Payment method', false],
  ['paymentType', 'Payment type', false],
  ['walletType', 'Wallet', false],
  ['failureCode', 'Our failure code', true],
  ['errorMessage', 'Message shown / received', false],
  ['durationMs', 'Took (ms)', false]
];

const KIND_LABELS = { CHECKOUT: 'Checkout', CONSULTATION: 'Consultation payment', ADDITIONAL: 'Additional payment' };

export default {
  props: {
    problemId: { type: Number, default: null }
  },
  data () {
    return { loading: false, error: '', d: null };
  },
  computed: {
    f () {
      return (this.d && this.d.failure) || {};
    },
    title () {
      return this.d ? 'Payment problem #' + this.f.id : 'Payment problem';
    },
    stripeRows () {
      const meta = (this.d && this.d.stripe && this.d.stripe.metadata) || {};
      return STRIPE_FIELDS
        .filter(([key]) => meta[key] !== undefined && meta[key] !== null && meta[key] !== '')
        .filter(([key]) => !(key === 'errorMessage' && meta[key] === this.f.message))
        .map(([key, label, code]) => ({ key, label, code, value: meta[key] }));
    },
    orderLink () {
      if (!this.d || !this.d.order) return '';
      return this.f.kind === 'CONSULTATION' ? '/order/payment-failed/' + this.d.order.id : '/order/' + this.d.order.id;
    }
  },
  watch: {
    problemId: { immediate: true, handler: 'load' }
  },
  methods: {
    load () {
      if (!this.problemId) return;
      this.loading = true;
      this.error = '';
      this.d = null;
      const id = this.problemId;
      this.$orderService.getPaymentProblem(id).then((res) => {
        if (id === this.problemId) this.d = (res && res.data) || null;
      }).catch((e) => {
        const data = e && e.response && e.response.data;
        this.error = e && e.code === 'ECONNABORTED'
          ? 'The server took too long to answer (over 20 seconds). It may be down or its database is stuck — try again shortly.'
          : (data && data.message) || 'Could not load this payment problem';
      }).finally(() => {
        this.loading = false;
      });
    },
    copyAll () {
      const text = JSON.stringify(this.d, null, 2);
      const done = () => this.$awn.success('All details copied');
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(done).catch(() => window.prompt('Copy the details', text));
      } else {
        window.prompt('Copy the details', text);
      }
    },
    kindLabel (kind) {
      return KIND_LABELS[kind] || kind;
    },
    sourceLabel (s) {
      return ({ CARD: 'Card', ONLINE: 'Card', GOOGLE_PAY: 'Google Pay', APPLE_PAY: 'Apple Pay',
        DOCTOR_COMPLETE: 'When doctor completed', PAYMENT_SERVICE_PREAUTH: 'Card pre-authorisation',
        PAYMENT_SERVICE_CAPTURE: 'Card capture' })[s] || this.humanise(s);
    },
    browser (ua) {
      if (!ua) return '—';
      const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'macOS'
        : /Windows/.test(ua) ? 'Windows' : '';
      const name = /Edg\//.test(ua) ? 'Edge' : /CriOS|Chrome\//.test(ua) ? 'Chrome' : /FxiOS|Firefox\//.test(ua) ? 'Firefox'
        : /Safari\//.test(ua) ? 'Safari' : 'Browser';
      return name + (os ? ' on ' + os : '');
    },
    source (j) {
      const parts = [j.utmSource, j.utmMedium, j.utmCampaign].filter(Boolean);
      if (parts.length) return parts.join(' / ');
      return j.referrer || j.entryPage || 'Direct';
    },
    humanise (v) {
      if (!v) return '';
      const s = String(v).replace(/_/g, ' ').toLowerCase();
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    pretty (obj) {
      return JSON.stringify(obj, null, 2);
    },
    money (pence) {
      return (pence / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
    },
    when (v) {
      return v ? moment(v).local().format('DD/MM/YY HH:mm:ss') : '—';
    },
    time (v) {
      return v ? moment(v).local().format('HH:mm:ss') : '';
    }
  }
};
</script>

<style scoped>
.ppd-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}
.ppd-kicker {
  color: #64748b;
  font-size: 0.85rem;
}
.ppd-title {
  margin: 0.2rem 0 0.5rem;
  color: #b42318;
  font-size: 1.4rem;
}
.ppd-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}
.ppd-code {
  background: #fdecea;
  color: #b42318;
  padding: 0.15rem 0.5rem;
  border-radius: 6px;
}
.ppd-advice {
  display: flex;
  gap: 0.6rem;
  background: #fdf3e2;
  color: #7a4a06;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
}
.ppd-advice .fa {
  font-size: 1.3rem;
  margin-top: 2px;
}
.ppd-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 0.75rem;
}
.ppd-card {
  border: 1px solid #dfe7e3;
  border-radius: 10px;
  padding: 0.85rem 1rem;
  min-width: 0;
}
.ppd-wide {
  grid-column: 1 / -1;
}
.ppd-card h4 {
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 0.6rem;
}
.ppd-dl {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.3rem 1rem;
  margin: 0;
  font-size: 0.88rem;
}
.ppd-dl dt {
  color: #64748b;
  font-weight: 500;
}
.ppd-dl dd {
  margin: 0;
  word-break: break-word;
}
.ppd-raw {
  margin-top: 0.75rem;
  font-size: 0.85rem;
}
.ppd-raw pre {
  background: #f6f8f7;
  border-radius: 6px;
  padding: 0.6rem;
  max-height: 260px;
  overflow: auto;
  margin: 0.4rem 0 0;
}
.ppd-tries {
  padding-left: 1.2rem;
  margin: 0;
  font-size: 0.88rem;
}
.ppd-tries li.current {
  font-weight: 600;
}
.ppd-timeline tr.ppd-hit td {
  background: #fdecea;
}
.ppd-timeline tr.ppd-bad td {
  background: #fff7f5;
}
</style>
